import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { signOut, switchRole } from '../api';
import { LoginScreen } from '../screens/login/login-screen';
import './auth-gate.css';

// Sign-in gate. On load the api-client tries GET /me, which 401s, triggers a
// cookie-backed /auth/refresh, and retries; if a valid session cookie exists the
// admin comes up already signed in. If not, this surface signs the admin in
// DIRECTLY via its own phone + OTP page (PRD §7.3 admin `/login`, DR-34) — on
// success the session reloads and the console renders.
//
// The console is admin-only. A shared session from the website (a customer or
// merchant who signed in there) is valid but must NOT reach the console. Every admin
// API is 403-gated server-side (requireRole); the gate also fails closed: it ends
// that session and shows the sign-in page with the design's no-access line
// (A §A.5 `noaccess`) — said only about a session that already exists, never about
// a typed number before verification (§4.1).
export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  const { user, loading, reload } = useSession();
  const [denied, setDenied] = useState(false);
  const nonAdmin = !!user && user.active_role !== 'admin';
  // One sign-in for every surface (client decision 30 Oct 2026): an account that holds
  // the admin role but whose shared session is in another role (opened from the
  // customer or merchant dashboard) takes the admin role here (API-13) instead of
  // being signed out. Only an account without it gets the no-access sign-in.
  const canSwitch = nonAdmin && (user?.roles ?? []).includes('admin');

  useEffect(() => {
    if (!nonAdmin) return;
    let live = true;
    if (canSwitch) {
      void switchRole('admin')
        .catch(() => signOut().catch(() => undefined).then(() => setDenied(true)))
        .then(() => {
          if (live) reload();
        });
      return () => {
        live = false;
      };
    }
    setDenied(true);
    void signOut()
      .catch(() => undefined)
      .then(() => {
        if (live) reload();
      });
    return () => {
      live = false;
    };
  }, [nonAdmin, canSwitch, reload]);

  if ((loading || canSwitch) && !denied) return <div className="auth-splash">{t('common.loading')}</div>;
  if (user && !nonAdmin) return <>{children}</>;
  return (
    <LoginScreen
      denied={denied}
      onSuccess={() => {
        setDenied(false);
        reload();
      }}
    />
  );
}
